import { useEffect, useRef, useState } from 'react'
import { api, ApiError } from '../lib/api'

/** PICTURES TO SEND (migration 068, item 24, 6 Oct 2026). The 📎 button and
 *  the row of small squares above a reply box -- used by a reply and by
 *  "New conversation" alike.
 *
 *  BIG PHOTOS ARE SHRUNK HERE, IN THE BROWSER, BEFORE THEY UPLOAD (his Q2 A):
 *  carriers take about 1 MB in a picture message, and a phone photo is 3-5 MB.
 *  Anything over TARGET, or wider than MAX_SIDE, is redrawn as a JPEG small
 *  enough to go and still sharp on a phone screen. A GIF is left as it is
 *  (redrawing would stop it moving) and must already fit. An iPhone's HEIC is
 *  turned into a JPEG by the phone itself when it is picked in a browser; a
 *  file this browser cannot read is refused in words.
 *
 *  Every class carries `pt-`: theme.css owns the short names. */

const TARGET = 900 * 1024
const HARD_LIMIT = 1_572_864
const MAX_SIDE = 1600
export const MAX_PICTURES = 10

export type Picture = {
  key: string
  preview: string
  /** The server's id once uploaded; null while it is still going up. */
  id: string | null
  size: number
  error: string | null
}

/** The picture, loaded. By its `load` event, NOT `img.decode()`: decode can
 *  wait for ever while the tab is in the background, which left a picture
 *  "Adding…" for good when the dispatcher looked at another window. */
function readImage(file: Blob): Promise<HTMLImageElement> {
  const url = URL.createObjectURL(file)
  return new Promise((resolve, reject) => {
    const img = new Image()
    const done = () => URL.revokeObjectURL(url)
    img.onload = () => { done(); resolve(img) }
    img.onerror = () => { done(); reject(new Error('unreadable')) }
    img.src = url
  })
}

function toBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob | null> {
  return new Promise((res) => canvas.toBlob(res, 'image/jpeg', quality))
}

/** The file as it will go: itself when it already fits, else a smaller JPEG. */
export async function shrink(file: File): Promise<Blob> {
  if (file.type === 'image/gif') {
    if (file.size > HARD_LIMIT) throw new Error('That GIF is too big for a picture message (1.5 MB at most).')
    return file
  }
  let img: HTMLImageElement
  try {
    img = await readImage(file)
  } catch {
    throw new Error('This picture cannot be read here. Save it as a JPG or PNG and attach it again.')
  }
  const side = Math.max(img.naturalWidth, img.naturalHeight)
  if (file.size <= TARGET && side <= MAX_SIDE && /^image\/(jpeg|png)$/.test(file.type)) return file
  for (const [maxSide, q] of [[MAX_SIDE, 0.82], [1280, 0.75], [1024, 0.7]] as const) {
    const scale = Math.min(1, maxSide / side)
    const canvas = document.createElement('canvas')
    canvas.width = Math.max(1, Math.round(img.naturalWidth * scale))
    canvas.height = Math.max(1, Math.round(img.naturalHeight * scale))
    const ctx = canvas.getContext('2d')
    if (!ctx) break
    // A PNG with see-through parts would turn black as a JPEG.
    ctx.fillStyle = '#fff'
    ctx.fillRect(0, 0, canvas.width, canvas.height)
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
    const out = await toBlob(canvas, q)
    if (out && out.size <= TARGET) return out
  }
  throw new Error('That picture could not be made small enough to send.')
}

/** The pictures waiting to go with one message, and the 📎 that adds them. */
/** A picture this screen did not just upload: one already kept by the
 *  server -- a scheduled group message's (070). Drawn from behind the session
 *  by its id. */
export type KeptPicture = { id: string; size: number }

export function usePictures(kept: KeptPicture[] = []) {
  const [pictures, setPictures] = useState<Picture[]>(() => kept.map((k) => ({
    key: k.id, preview: `/api/messaging/sent-pictures/${k.id}`, id: k.id,
    size: k.size, error: null,
  })))
  /** The picture keys in the tray NOW, readable by an upload still running. */
  const live = useRef<Set<string>>(new Set(kept.map((k) => k.id)))
  /** Bumped by `clear`: a pick made for the last conversation stops adding. */
  const generation = useRef(0)
  const urls = useRef<Map<string, string>>(new Map())
  useEffect(() => () => { urls.current.forEach((u) => URL.revokeObjectURL(u)) }, [])

  function drop(keys: string[]) {
    for (const k of keys) {
      const u = urls.current.get(k)
      if (u) URL.revokeObjectURL(u)
      urls.current.delete(k)
      live.current.delete(k)
    }
  }

  async function add(files: FileList | null) {
    if (!files) return
    const mine = generation.current
    for (const file of Array.from(files)) {
      // TEN AT MOST, counted from what is in the tray NOW -- two quick picks
      // must not both see room for ten.
      if (generation.current !== mine || live.current.size >= MAX_PICTURES) break
      const key = `${Date.now()}-${Math.random()}`
      const preview = URL.createObjectURL(file)
      urls.current.set(key, preview)
      live.current.add(key)
      setPictures((p) => [...p, { key, preview, id: null, size: file.size, error: null }])
      try {
        const blob = await shrink(file)
        // A PICTURE NEVER FOLLOWS THE DISPATCHER TO ANOTHER CONVERSATION (the
        // review of 29687cc): if the tray was cleared or this one removed
        // while it shrank, it is not uploaded, and the rest of the pick stops.
        if (generation.current !== mine || !live.current.has(key)) break
        const up = await api.messagingUploadPicture(blob)
        if (generation.current !== mine) break
        setPictures((p) => p.map((x) => x.key === key ? { ...x, id: up.id, size: up.size } : x))
      } catch (e) {
        if (generation.current !== mine) break
        const msg = e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Could not attach it.'
        setPictures((p) => p.map((x) => x.key === key ? { ...x, error: msg } : x))
      }
    }
  }
  const remove = (key: string) => {
    drop([key])
    setPictures((p) => p.filter((x) => x.key !== key))
  }
  const clear = () => {
    generation.current += 1
    drop([...live.current])
    setPictures([])
  }
  const ids = pictures.filter((p) => p.id && !p.error).map((p) => p.id as string)
  const busy = pictures.some((p) => !p.id && !p.error)
  return { pictures, add, remove, clear, ids, busy }
}

export function ClipButton({ onFiles, disabled }: {
  onFiles: (files: FileList | null) => void
  disabled?: boolean
}) {
  const input = useRef<HTMLInputElement>(null)
  return (
    <>
      <button type="button" className="pt-clip" title="Attach a picture" aria-label="Attach a picture"
              disabled={disabled} onClick={() => input.current?.click()}>
        {/* Drawn, not an emoji: Windows renders 📎 as a smudge. */}
        <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" fill="none"
             stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 11.5l-8.6 8.6a5.5 5.5 0 0 1-7.8-7.8l8.6-8.6a3.7 3.7 0 0 1 5.2 5.2l-8.6 8.6a1.8 1.8 0 0 1-2.6-2.6l7.9-7.9" />
        </svg>
      </button>
      <input ref={input} type="file" hidden multiple
             accept="image/jpeg,image/png,image/gif,image/heic,image/heif,image/webp"
             onChange={(e) => { onFiles(e.target.files); e.target.value = '' }} />
    </>
  )
}

export function PictureRow({ pictures, onRemove }: {
  pictures: Picture[]
  onRemove: (key: string) => void
}) {
  if (pictures.length === 0) return null
  return (
    <div className="pt-row">
      {pictures.map((p) => (
        <div key={p.key} className={`pt-thumb ${p.error ? 'pt-bad' : ''}`} title={p.error ?? undefined}>
          <img src={p.preview} alt="" />
          <button type="button" className="pt-x" aria-label="Remove this picture" onClick={() => onRemove(p.key)}>✕</button>
          <span className="pt-sz">{p.error ? 'Not attached' : !p.id ? 'Adding…' : `${Math.max(1, Math.round(p.size / 1024))} KB`}</span>
        </div>
      ))}
      {pictures.some((p) => p.error) && (
        <div className="pt-err">{pictures.find((p) => p.error)?.error}</div>
      )}
    </div>
  )
}
